import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';

import { ScreenerSettingName } from '@/hooks/screener-settings/screenerSettingName';
import { useScreenerSettingFindOne } from '@/pages/CRM/Screener/hooks/useScreenerSettingsQuery';
import { generatePathWithDomain } from '@/utils/generatePathWithDomain';

interface RawScreenerSettingRouteGuardProps {
  requiredValue: string;
  redirectTo: string;
  value: string | undefined;
  isLoading: boolean;
  isError: boolean;
  children: ReactNode;
}

export const RawScreenerSettingRouteGuard = ({
  requiredValue,
  redirectTo,
  value,
  isLoading,
  isError,
  children
}: RawScreenerSettingRouteGuardProps) => {
  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" flex={1}>
        <CircularProgress />
      </Stack>
    );
  }

  if (isError) {
    return <Navigate to={generatePathWithDomain(redirectTo)} replace />;
  }

  return value === requiredValue ? (
    <>{children}</>
  ) : (
    <Navigate to={generatePathWithDomain(redirectTo)} replace />
  );
};

interface ScreenerSettingRouteGuardProps {
  settingName: ScreenerSettingName;
  requiredValue: string;
  redirectTo: string;
  children: ReactNode;
}

export const ScreenerSettingRouteGuard = ({
  settingName,
  ...props
}: ScreenerSettingRouteGuardProps) => {
  const { data, isLoading, isError } = useScreenerSettingFindOne({
    settingName
  });

  return (
    <RawScreenerSettingRouteGuard
      {...props}
      value={data?.data?.value}
      isLoading={isLoading}
      isError={isError}
    />
  );
};
